/**
 * /debug/soak-test ✦ iter207
 * ─────────────────────────────────────────────────────────────
 * Soak test instrumentation surface. Lets a human operator run
 * the 2-hour unified soak across the four highest emotional-risk
 * surfaces (Lens Notes, Guestbook, Pocket Booth, Memory Snap)
 * while the telemetry layer records:
 *
 *   • `eventTimelineSignature` — canonical lifecycle rhythm
 *   • lifecycle counters (cam:start, mic:start, recorder pairs,
 *     track-ended events, devicechange transitions, interruptions,
 *     visibility hides, cumulative bytes)
 *   • memory delta samples (Chrome only; absent on Safari)
 *   • environmental conditions at report time (battery, save-data,
 *     thermal where exposed, visibility state)
 *
 * The "Run Cycle ✦" button runs one scripted lifecycle:
 *   pipeline.start() → recorder.start() → wait N seconds →
 *   recorder.stop() → pipeline.stop(). Repeat manually or hold
 *   the "Auto-loop ✦" toggle for autonomous cycling.
 *
 * Export Report ✦ downloads a JSON sidecar the operator pastes
 * back to the team for diagnosis.
 *
 * Hidden from production navigation. Direct URL access only.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useBoothCameraPipeline } from '../../hooks/useBoothCameraPipeline';
import { useParticipationAudioPipeline } from '../../hooks/useParticipationAudioPipeline';
import { BOOTH_CAMERA_PROFILES } from '../../lib/booth-os/boothCameraProfiles';
import { PARTICIPATION_AUDIO_PROFILES } from '../../lib/booth-os/participationAudioProfiles';
import {
  enable as enableSoak,
  disable as disableSoak,
  emit as soakEmit,
  subscribe as subscribeSoak,
  snapshot as soakSnapshot,
  exportReport as buildSoakReport,
  captureConditions as captureSoakConditions,
} from '../../lib/booth-os/soakTelemetry';

const CAMERA_PROFILES = Object.keys(BOOTH_CAMERA_PROFILES);
const AUDIO_PROFILES = Object.keys(PARTICIPATION_AUDIO_PROFILES);

const PANEL_BG = 'rgba(0,0,0,0.4)';
const GOLD_BORDER = '1px solid rgba(212,175,94,0.18)';
const IVORY = '#f8e8c8';
const IVORY_FADED = 'rgba(248,232,200,0.55)';
const SOAK_STORAGE_KEY = 'eventdrop.soak.enabled';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const Pill = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    style={{
      padding: '8px 14px',
      borderRadius: 999,
      border: '1px solid rgba(212,175,94,0.35)',
      background: active ? 'rgba(212,175,94,0.18)' : 'transparent',
      color: active ? IVORY : 'rgba(248,232,200,0.65)',
      fontFamily: 'Georgia, serif', fontStyle: 'italic',
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);

const Action = ({ onClick, children, testId, disabled, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    style={{
      padding: '10px 18px',
      borderRadius: 999,
      border: `1px solid ${danger ? 'rgba(224,57,42,0.6)' : 'rgba(212,175,94,0.35)'}`,
      background: danger ? 'rgba(224,57,42,0.10)' : 'rgba(212,175,94,0.10)',
      color: danger ? '#e0392a' : IVORY,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      fontFamily: 'Georgia, serif', fontStyle: 'italic',
      letterSpacing: '0.04em', fontSize: 13,
    }}
  >{children}</button>
);

const Row = ({ k, v, testId }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '160px 1fr',
    gap: 12,
    padding: '4px 0',
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: 12,
    color: IVORY,
    borderBottom: '1px solid rgba(212,175,94,0.06)',
  }}>
    <span style={{ color: IVORY_FADED }}>{k}</span>
    <span data-testid={testId} style={{ textAlign: 'right' }}>{String(v)}</span>
  </div>
);

const SoakTestPage = () => {
  const [enabled, setEnabled] = useState(false);
  const [cameraProfile, setCameraProfile] = useState('guestbook');
  const [audioProfile, setAudioProfile] = useState('voice_message');
  const [cycleSec, setCycleSec] = useState(5);
  const [autoLoop, setAutoLoop] = useState(false);
  const [cycleRunning, setCycleRunning] = useState(false);
  const [statusMsg, setStatusMsg] = useState('idle');
  const [snap, setSnap] = useState(() => soakSnapshot());
  const [conditions, setConditions] = useState(null);

  const cam = useBoothCameraPipeline({ profile: cameraProfile });
  const mic = useParticipationAudioPipeline({ profile: audioProfile });

  const frameRef = useRef(null);
  const loopAbortRef = useRef(false);

  // Live subscription — re-render whenever the telemetry collector
  // pushes a new event. Bounded ring keeps this cheap.
  useEffect(() => {
    const unsub = subscribeSoak(() => setSnap(soakSnapshot()));
    return unsub;
  }, []);

  // Capture environmental conditions every 10s while enabled.
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const tick = async () => {
      const c = await captureSoakConditions();
      if (!cancelled) setConditions(c);
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);

  // Honour persisted soak-enabled flag across reloads — long soak
  // tests survive a Safari refresh and resume telemetry collection.
  useEffect(() => {
    try {
      if (localStorage.getItem(SOAK_STORAGE_KEY) === '1') {
        enableSoak();
        setEnabled(true);
      }
    } catch { /* localStorage may be blocked in incognito */ }
    return () => {
      // never disable on unmount — keep counters running across
      // route changes during the soak. Operator must explicitly stop.
    };
  }, []);

  const handleEnable = () => {
    enableSoak();
    setEnabled(true);
    try { localStorage.setItem(SOAK_STORAGE_KEY, '1'); } catch {}
  };
  const handleDisable = () => {
    disableSoak();
    setEnabled(false);
    try { localStorage.setItem(SOAK_STORAGE_KEY, '0'); } catch {}
    setAutoLoop(false);
  };

  // ── Cycle: start camera + mic → record N seconds → stop both.
  const runCycle = async () => {
    if (cycleRunning) return;
    setCycleRunning(true);
    setStatusMsg('cycle:starting');
    soakEmit('soak:cycle', { camera: cameraProfile, audio: audioProfile, durationSec: cycleSec });
    try {
      const camStream = await cam.start();
      const micStream = await mic.start();
      if (!camStream && !micStream) {
        setStatusMsg('cycle:fail-acquisition');
        cam.stop(); mic.stop();
        setCycleRunning(false);
        return;
      }
      setStatusMsg('cycle:recording');
      // Both recorders for cross-pipeline pair counting.
      cam.startRecording();
      mic.startRecording();
      await new Promise((r) => setTimeout(r, Math.max(1, cycleSec) * 1000));
      const camRes = await cam.stopRecording();
      const micRes = await mic.stopRecording();
      // Tiny upload simulation — we don't actually POST, but the
      // soak run wants the upload:begin / upload:end rhythm in the
      // timeline so disk + network teardown gets exercised at
      // the right beat. (Live operator can flip this to a real
      // upload in a future iteration if needed.)
      soakEmit('upload:begin', { bytes: (camRes?.blob?.size || 0) + (micRes?.rawBlob?.size || 0) });
      await new Promise((r) => setTimeout(r, 200));
      soakEmit('upload:end', { bytes: (camRes?.blob?.size || 0) + (micRes?.rawBlob?.size || 0) });
      cam.stop(); mic.stop();
      setStatusMsg('cycle:complete');
    } catch (e) {
      setStatusMsg(`cycle:error ${e?.message || ''}`);
    } finally {
      setCycleRunning(false);
    }
  };

  // Auto-loop — keeps cycling until the operator toggles it off
  // or hits Disable. Cheap pacing wait between cycles so the
  // browser has a beat to release before the next start.
  useEffect(() => {
    if (!autoLoop) { loopAbortRef.current = true; return undefined; }
    loopAbortRef.current = false;
    let cancelled = false;
    (async () => {
      while (!cancelled && !loopAbortRef.current) {
        await runCycle();
        if (cancelled || loopAbortRef.current) break;
        await new Promise((r) => setTimeout(r, 1500));
      }
    })();
    return () => { cancelled = true; loopAbortRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoop]);

  const exportReport = async () => {
    const report = await buildSoakReport({ note: `soak.camera=${cameraProfile}.audio=${audioProfile}` });
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eventdrop-soak-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const c = snap.counters;
  const memLatest = snap.memorySamples[snap.memorySamples.length - 1];
  const memFirst = snap.memorySamples[0];
  const memDeltaMb = memLatest && memFirst
    ? ((memLatest.used - memFirst.used) / (1024 * 1024)).toFixed(2)
    : null;

  return (
    <div
      data-testid="soak-test-page"
      style={{
        minHeight: '100vh', background: '#08070a', color: IVORY,
        padding: '40px 28px 80px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <header style={{ maxWidth: 1180, margin: '0 auto 24px' }}>
        <p style={{
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          color: IVORY_FADED, margin: 0,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          fontSize: 12,
        }}>EventDrop ✦ Internal</p>
        <h1 style={{
          fontFamily: 'Georgia, "Cormorant Garamond", serif',
          fontWeight: 500, fontSize: 36, margin: '4px 0 6px',
          color: IVORY,
        }}>Soak test</h1>
        <p style={{ margin: 0, color: IVORY_FADED, fontSize: 14, maxWidth: 880 }}>
          Long-session preservation reliability. Run cycles or auto-loop while
          the telemetry collector captures the canonical lifecycle rhythm,
          counters, memory deltas, and environmental conditions. Export the
          JSON sidecar at the end of the soak for diagnosis.
        </p>
      </header>

      <main style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
        {/* LEFT — controls + live preview */}
        <section data-testid="soak-controls" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {enabled
                ? <Action testId="soak-disable" onClick={handleDisable} danger>Stop telemetry ✦</Action>
                : <Action testId="soak-enable" onClick={handleEnable}>Enable telemetry ✦</Action>}
              <Action testId="soak-run-cycle" onClick={runCycle} disabled={!enabled || cycleRunning}>
                Run cycle ✦
              </Action>
              <Action testId="soak-auto-loop"
                onClick={() => setAutoLoop((v) => !v)}
                disabled={!enabled}>
                {autoLoop ? 'Pause auto-loop ✦' : 'Auto-loop ✦'}
              </Action>
              <Action testId="soak-export" onClick={exportReport} disabled={!enabled && snap.ring.length === 0}>
                Export report ✦
              </Action>
            </div>
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: IVORY_FADED, fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                cycle duration (seconds)
              </span>
              <input
                type="number" min={1} max={30} value={cycleSec}
                onChange={(e) => setCycleSec(Math.max(1, parseInt(e.target.value || '5', 10)))}
                data-testid="soak-cycle-duration"
                style={{
                  width: 70, padding: '6px 8px', borderRadius: 8,
                  border: '1px solid rgba(212,175,94,0.35)',
                  background: 'transparent', color: IVORY,
                  fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13,
                }}
              />
              <span data-testid="soak-status" style={{ marginLeft: 'auto', color: IVORY_FADED, fontSize: 12 }}>
                {statusMsg}
              </span>
            </div>
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }}>
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              profiles ✦
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={{ color: IVORY_FADED, fontSize: 12, marginBottom: 6, fontFamily: 'ui-monospace, Menlo, monospace' }}>camera profile</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CAMERA_PROFILES.map((p) => (
                    <Pill key={p} active={p === cameraProfile} onClick={() => setCameraProfile(p)} testId={`soak-cam-${p}`}>
                      {p}
                    </Pill>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ color: IVORY_FADED, fontSize: 12, marginBottom: 6, fontFamily: 'ui-monospace, Menlo, monospace' }}>audio profile</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {AUDIO_PROFILES.map((p) => (
                    <Pill key={p} active={p === audioProfile} onClick={() => setAudioProfile(p)} testId={`soak-mic-${p}`}>
                      {p}
                    </Pill>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div ref={frameRef} style={{
            position: 'relative', aspectRatio: String(cam.profile.aspectRatio),
            background: '#000', borderRadius: 10, overflow: 'hidden',
            border: GOLD_BORDER,
          }}>
            <video ref={cam.videoRef} playsInline muted style={cam.previewStyle} data-testid="soak-video-preview" />
          </div>
        </section>

        {/* RIGHT — counters + lifecycle ring */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }} data-testid="soak-counters">
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              counters ✦
            </h3>
            <Row k="cycles" v={c.cycles} testId="soak-counter-cycles" />
            <Row k="cam:start" v={c.streamStartCamera} testId="soak-counter-cam-start" />
            <Row k="cam:restart" v={c.streamRestartsCamera} testId="soak-counter-cam-restart" />
            <Row k="cam:rec-pairs" v={c.recorderPairsCamera} testId="soak-counter-cam-recpairs" />
            <Row k="cam:track-ended" v={c.trackEndedCamera} testId="soak-counter-cam-trackended" />
            <Row k="cam:devicechange" v={c.deviceChangesCamera} testId="soak-counter-cam-devicechange" />
            <Row k="mic:start" v={c.streamStartMic} testId="soak-counter-mic-start" />
            <Row k="mic:restart" v={c.streamRestartsMic} testId="soak-counter-mic-restart" />
            <Row k="mic:rec-pairs" v={c.recorderPairsMic} testId="soak-counter-mic-recpairs" />
            <Row k="mic:track-ended" v={c.trackEndedMic} testId="soak-counter-mic-trackended" />
            <Row k="mic:devicechange" v={c.deviceChangesMic} testId="soak-counter-mic-devicechange" />
            <Row k="mic:interrupt" v={c.interruptionsMic} testId="soak-counter-mic-interrupt" />
            <Row k="doc:hidden" v={c.visibilityHides} testId="soak-counter-doc-hidden" />
            <Row k="upload:began" v={c.uploadBegan} testId="soak-counter-upload-began" />
            <Row k="upload:end" v={c.uploadCompleted} testId="soak-counter-upload-end" />
            <Row k="upload:fail" v={c.uploadFailed} testId="soak-counter-upload-fail" />
            <Row k="totalBytes" v={fmtBytes(c.totalBytes)} testId="soak-counter-bytes" />
            <Row k="memory Δ" v={memDeltaMb !== null ? `${memDeltaMb} MB` : '— (Safari)'} testId="soak-memory-delta" />
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }} data-testid="soak-conditions">
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              conditions ✦
            </h3>
            {conditions ? (
              <>
                <Row k="visibility" v={conditions.visibilityState} />
                <Row k="batteryLevel" v={conditions.batteryLevel ?? '—'} />
                <Row k="batteryCharging" v={conditions.batteryCharging ?? '—'} />
                <Row k="saveData" v={conditions.saveData ?? '—'} />
                <Row k="effectiveType" v={conditions.effectiveType ?? '—'} />
                <Row k="thermalState" v={conditions.thermalState ?? '—'} />
                <Row k="deviceMemoryGB" v={conditions.deviceMemoryGB ?? '—'} />
                <Row k="hardwareConcurrency" v={conditions.hardwareConcurrency ?? '—'} />
                <Row k="devicePixelRatio" v={conditions.devicePixelRatio ?? '—'} />
                <Row k="onLine" v={conditions.onLine ?? '—'} />
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 12, color: IVORY_FADED }}>
                enable telemetry to capture environmental conditions.
              </p>
            )}
          </div>

          <div style={{ padding: 18, border: GOLD_BORDER, borderRadius: 14, background: PANEL_BG }}>
            <h3 style={{ margin: '0 0 8px', color: '#c19a4b', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: 18 }}>
              eventTimelineSignature ✦
            </h3>
            <div
              data-testid="soak-timeline"
              style={{
                maxHeight: 280, overflowY: 'auto',
                fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11,
                color: IVORY,
              }}
            >
              {snap.ring.length === 0 ? (
                <p style={{ margin: 0, color: IVORY_FADED }}>—</p>
              ) : (
                snap.ring.slice(-80).reverse().map((e, i) => (
                  <div key={`${e.t}-${i}`} style={{ padding: '2px 0', borderBottom: '1px solid rgba(212,175,94,0.06)' }}>
                    <span style={{ color: IVORY_FADED }}>{String(e.t).padStart(7, ' ')}ms</span>{' '}
                    <span>{e.evt}</span>
                    {e.meta && (
                      <span style={{ color: IVORY_FADED }}>
                        {' '}{Object.entries(e.meta).map(([k, v]) => `${k}=${typeof v === 'number' && k === 'bytes' ? fmtBytes(v) : v}`).join(' ')}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SoakTestPage;
